import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import apiClient from "../api/client";
import { AuthContext } from "../App";

export default function ArcadeInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const getCurrentReturnPath = () => {
    const p = window.location.pathname || "/";
    const s = window.location.search || "";
    return `${p}${s}`;
  };

  const setPostAuthReturn = () => {
    try {
      localStorage.setItem("post_auth_redirect", getCurrentReturnPath());
    } catch (_) {}
  };

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [hasTyped, setHasTyped] = useState(false);
  const [vsPop, setVsPop] = useState(false);
  const [vsPopDone, setVsPopDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [revealReady, setRevealReady] = useState(false);
  const vsPopTimerRef = useRef(null);

  const initiatorName = useMemo(() => {
    const n =
      invite?.initiator_name ||
      invite?.initiatorName ||
      invite?.invited_by ||
      invite?.created_by_name ||
      "";
    return (typeof n === "string" ? n : "").trim();
  }, [invite]);

  const inviterDisplay = initiatorName || "Someone";

  // Backend returns revealed answers under `reveal` once claimed/eligible.
  // Keep fallbacks for older shapes.
  const initiatorAnswer =
    invite?.reveal?.initiator_answer ??
    invite?.reveal?.initiatorAnswer ??
    invite?.initiator_answer ??
    invite?.initiatorAnswer ??
    invite?.initiator_response ??
    invite?.initiatorResponse ??
    invite?.creator_answer ??
    invite?.creatorAnswer ??
    invite?.initiator?.answer ??
    invite?.initiator?.response ??
    invite?.answers?.initiator ??
    invite?.answers?.initiator_answer ??
    invite?.answers?.initiatorAnswer ??
    null;

  const inviteeAnswer =
    invite?.reveal?.invitee_answer ??
    invite?.reveal?.inviteeAnswer ??
    invite?.invitee_answer ??
    invite?.inviteeAnswer ??
    invite?.invitee_response ??
    invite?.inviteeResponse ??
    invite?.invitee?.answer ??
    invite?.invitee?.response ??
    invite?.answers?.invitee ??
    invite?.answers?.invitee_answer ??
    invite?.answers?.inviteeAnswer ??
    invite?.response ??
    null;

  const hasBothAnswers = Boolean(
    typeof initiatorAnswer === "string" &&
      initiatorAnswer.trim().length > 0 &&
      typeof inviteeAnswer === "string" &&
      inviteeAnswer.trim().length > 0
  );

  const currentUserId = user?.id || user?._id || user?.user_id || null;
  const initiatorUserId =
    invite?.initiator_user_id ?? invite?.initiatorUserId ?? invite?.initiator?.id ?? invite?.initiator?.user_id ?? null;
  const inviteeUserId =
    invite?.invitee_user_id ?? invite?.inviteeUserId ?? invite?.invitee?.id ?? invite?.invitee?.user_id ?? null;

  const isInitiator = Boolean(
    invite?.viewer?.is_initiator ||
    (currentUserId && initiatorUserId && currentUserId === initiatorUserId)
  );
  const isInvitee = Boolean(
    invite?.viewer?.is_invitee ||
    (currentUserId && inviteeUserId && currentUserId === inviteeUserId)
  );

  const hasInviteeAnswer = Boolean(
    invite?.reveal?.invitee_answer ??
    invite?.invitee_answer ??
    invite?.inviteeAnswer ??
    invite?.invitee_response ??
    invite?.inviteeResponse ??
    invite?.answers?.invitee ??
    invite?.answers?.invitee_answer
  );

  const isMatched = Boolean(invite?.match_id || invite?.state === "completed");

  const isAnsweredOrReady = Boolean(
    hasInviteeAnswer && !isMatched
  );

  // Initiator should never "claim". Claim is for invitee to bind invite to their account.
  // If initiator wants to see the reveal, they should just refresh the invite data (GET).
  async function refreshRevealOnly() {
    setClaiming(true);
    setError("");
    setNotice("");
    try {
      const res = await apiClient.get(`/arcade/invite/${token}`);
      setInvite(res.data || null);
      if (!(res.data?.reveal?.initiator_answer && res.data?.reveal?.invitee_answer)) {
        setNotice("Reveal isn't available yet. It unlocks once the other person finishes their side.");
      }
    } catch (e) {
      setError("Could not refresh reveal.");
    } finally {
      setClaiming(false);
    }
  }

  async function fetchInvite() {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const res = await apiClient.get(`/arcade/invite/${token}`);
      setInvite(res.data);
      setRevealReady(false);
    } catch (e) {
      setError("This invite is no longer available.");
    } finally {
      setLoading(false);
    }
  }

  async function claimReveal() {
    if (!user) return;
    setClaiming(true);
    setError("");
    setNotice("");
    try {
      if (isInitiator) {
        // Initiator cannot claim by design
        await refreshRevealOnly();
        return;
      }
      await apiClient.post(`/arcade/invite/${token}/claim`, {});
      const res = await apiClient.get(`/arcade/invite/${token}`);
      setInvite(res.data);
      setRevealReady(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "INITIATOR_CANNOT_CLAIM") {
        setError("This is your invite. The other person must finish their side to unlock the reveal.");
      } else {
        setError(detail || "Unable to reveal answers yet.");
      }
    } finally {
      setClaiming(false);
    }
  }

  async function continueToMatch() {
    if (!user) return;
    setContinuing(true);
    setError("");
    try {
      const res = await apiClient.post(`/arcade/invite/${token}/continue`, {});
      const redirect = res?.data?.redirect || "/dashboard";
      navigate(redirect, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to continue right now.");
    } finally {
      setContinuing(false);
    }
  }

  useEffect(() => {
    return () => {
      if (vsPopTimerRef.current) clearTimeout(vsPopTimerRef.current);
    };
  }, []);

  useEffect(() => {
    fetchInvite();
  }, [token]);

  async function handleSubmit() {
    if (!answer.trim()) return;
    setSubmitting(true);
    setNotice("");
    try {
      // Hard guard rails: do not let initiator or completed invites respond
      if (isInitiator) {
        setError("This is your invite. The other person answers here.");
        return;
      }
      if (isMatched) {
        setError("This Spark is already matched. Continue in your dashboard.");
        return;
      }
      if (hasInviteeAnswer) {
        setError("This invite already has a response.");
        return;
      }

      await apiClient.post(`/arcade/invite/${token}/respond`, {
        answer,
      });
      setSubmitted(true);
      await fetchInvite();
      if (user) {
        await claimReveal();
      }
    } catch (err) {
      const code = err?.response?.data?.detail || err?.response?.data?.code || "";
      if (code === "INVITE_ALREADY_ANSWERED") {
        setError("Someone already answered this invite.");
      } else if (code === "INVITE_EXPIRED") {
        setError("This invite expired.");
      } else if (code === "INVITE_COMPLETED") {
        setError("This Spark is already completed. Check your dashboard.");
      } else {
        setError("Unable to submit response.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 px-4 py-10 ul-cosmic">
      <div className="max-w-lg mx-auto">
        <div className="ul-cosmic-bg p-5 sm:p-7">
          <div className="ul-cosmic-stars" />
          <div className="ul-cosmic-arc" />
          <div className="relative">

        {notice ? (
          <div className="mb-4 rounded-2xl bg-black/[0.18] border border-white/10 px-4 py-3 text-sm ul-cosmic-muted">
            {notice}
          </div>
        ) : null}

        {/* Initiator opening an answered/ready invite: allow reveal via GET refresh (no claim, no re-answer) */}
        {isInitiator && isAnsweredOrReady && !isMatched ? (
          <div className="mb-4 ul-cosmic-card p-4">
            <div className="text-sm font-extrabold text-white">They answered</div>
            <div className="mt-1 text-sm ul-cosmic-muted">
              Tap reveal to unlock both answers.
            </div>

            {hasBothAnswers ? (
              <div className="mt-4 grid gap-3">
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                  <div className="text-xs font-extrabold tracking-widest text-white/65">YOUR ANSWER</div>
                  <div className="mt-2 text-white">{initiatorAnswer}</div>
                </div>
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                  <div className="text-xs font-extrabold tracking-widest text-white/65">THEIR ANSWER</div>
                  <div className="mt-2 text-white">{inviteeAnswer}</div>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <button
                  onClick={refreshRevealOnly}
                  className="w-full ul-cosmic-btn"
                  disabled={claiming}
                >
                  {claiming ? "Revealing\u2026" : "Reveal both answers"}
                </button>
                {error ? (
                  <div className="mt-2 text-sm text-rose-200">{error}</div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {/* If initiator opens a matched invite, don't show claim/respond UI */}
        {isInitiator && isMatched ? (
          <div className="ul-cosmic-card p-4">
            <div className="text-sm font-extrabold text-white">This Spark already matched</div>
            <div className="mt-1 text-sm ul-cosmic-muted">
              You're in the normal UnityLOVE flow now.
            </div>

            {hasBothAnswers ? (
              <div className="mt-4 grid gap-3">
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                  <div className="text-xs font-extrabold tracking-widest text-white/65">YOUR ANSWER</div>
                  <div className="mt-2 text-white">{initiatorAnswer}</div>
                </div>
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                  <div className="text-xs font-extrabold tracking-widest text-white/65">THEIR ANSWER</div>
                  <div className="mt-2 text-white">{inviteeAnswer}</div>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <button
                  onClick={refreshRevealOnly}
                  className="w-full ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110 py-3"
                  disabled={claiming}
                >
                  {claiming ? "Refreshing\u2026" : "Refresh reveal"}
                </button>
                {error ? (
                  <div className="mt-2 text-sm text-rose-200">{error}</div>
                ) : null}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex-1 ul-cosmic-btn text-sm"
              >
                Go to Dashboard
              </button>
              <Link
                to="/arcade"
                className="flex-1 ul-cosmic-pill text-center text-sm font-extrabold text-white/90 hover:brightness-110 py-3"
              >
                Back to Arcade
              </Link>
            </div>
          </div>
        ) : (isInitiator && isAnsweredOrReady && !isMatched && hasBothAnswers) ? (
          /* Initiator viewing revealed answers — show ONLY the reveal, NOT the prompt card again */
          null
        ) : (
        <>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-2 rounded-2xl ul-cosmic-panel px-4 py-3">
            <div className="w-9 h-9 rounded-2xl bg-white/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white/80" />
            </div>
            <div className="text-left">
              <div className="text-sm font-extrabold text-white">
                You've been invited to a Spark
              </div>
              <div className="text-xs ul-cosmic-muted">
                They answered first. Now it's your turn.
              </div>
            </div>
          </div>
          <div className="mt-3 text-sm ul-cosmic-muted">
            Answer first. Both responses unlock together.
          </div>
        </div>

        {/* Step Indicator */}
        <div className="text-center text-xs font-extrabold text-white/60 mb-4">
          {revealReady ? (
            <span>Step 2 of 2 <span className="ml-2">{'\u25CF\u25CF'}</span></span>
          ) : (
            <span>Step 1 of 2 <span className="ml-2">{'\u25CF\u25CB'}</span></span>
          )}
        </div>

        {/* Prompt Card */}
        <div className="ul-cosmic-card p-6">

          <div className="text-xs font-extrabold tracking-wide text-white/70 mb-2">
            PROMPT
          </div>

          <div className="text-xl leading-snug font-extrabold text-white mb-2">
            {invite?.prompt_text}
          </div>

          <div className="text-sm ul-cosmic-muted mb-6">
            <span className="font-extrabold text-white">{inviterDisplay}</span>{" "}
            already answered this.
          </div>

          {/* Duel Layout */}
          {!revealReady && (
            <>
              <div
                className={
                  "rounded-2xl bg-black/[0.18] border p-4 mb-4 transition " +
                  (hasTyped
                    ? "border-fuchsia-400/40 shadow-sm"
                    : "border-white/10")
                }
              >
                <div className="flex items-center gap-2 text-sm font-extrabold text-white">
                  <Lock className="w-4 h-4 text-white/70" />
                  Their Answer
                </div>
                <div className="text-sm text-white/60 mt-1">
                  {hasTyped
                    ? "Unlocking\u2026 keep going."
                    : `You won't see what ${inviterDisplay} wrote until you respond.`}
                </div>
              </div>

              <div className="relative my-4">
                <div className="h-px bg-white/10" />
                <div className="absolute left-1/2 -translate-x-1/2 -top-3">
                  <div
                    className={
                      "px-3 py-1 rounded-full text-xs font-black tracking-wide bg-black/30 " +
                      "border border-white/10 text-white/80 " +
                      "motion-safe:transition-transform motion-safe:duration-200 " +
                      (vsPop ? "motion-safe:scale-110 ring-2 ring-fuchsia-300/40" : "motion-safe:scale-100")
                    }
                  >
                    VS
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                <div className="text-xs font-extrabold tracking-wide text-white/65 uppercase mb-2">
                  Your Move
                </div>

                <textarea
                  rows={4}
                  value={answer}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAnswer(v);
                    const trimmedLen = v.trim().length;
                    if (!hasTyped && trimmedLen > 0) setHasTyped(true);
                    if (hasTyped && trimmedLen === 0) setHasTyped(false);

                    if (!vsPopDone && trimmedLen > 0) {
                      setVsPopDone(true);
                      setVsPop(true);
                      if (vsPopTimerRef.current) clearTimeout(vsPopTimerRef.current);
                      vsPopTimerRef.current = setTimeout(() => setVsPop(false), 220);
                    }
                  }}
                  placeholder="Type your answer..."
                  className="w-full rounded-2xl border border-white/10 bg-black/20 p-3 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-fuchsia-300/30"
                />

                <div className="text-xs text-white/60 mt-2">
                  Short. Real. First instinct.
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!answer.trim() || submitting}
                className={
                  "w-full mt-6 ul-cosmic-btn " +
                  (!answer.trim() || submitting
                    ? "opacity-50 cursor-not-allowed"
                    : "")
                }
              >
                {submitting ? "Submitting..." : "Lock In My Answer"}
              </button>

              {/* After submit (logged out): show signup gate */}
              {submitted && !user && (
                <div className="mt-6 rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                  <div className="text-sm font-extrabold text-white">
                    Your answer is saved.
                  </div>
                  <div className="mt-1 text-sm ul-cosmic-muted">
                    Create an account to reveal both answers and continue.
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-2">
                    <button
                      onClick={() => {
                        setPostAuthReturn();
                        navigate(`/auth?mode=signup`);
                      }}
                      className="w-full ul-cosmic-btn"
                    >
                      Sign up to reveal
                    </button>
                    <button
                      onClick={() => {
                        setPostAuthReturn();
                        navigate(`/auth?mode=login`);
                      }}
                      className="w-full ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110 py-3"
                    >
                      I already have an account
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Reveal + Continue */}
          {user && (revealReady || hasBothAnswers) && (
            <div className="mt-6">
              {!revealReady ? (
                <button
                  onClick={claimReveal}
                  disabled={claiming}
                  className="w-full ul-cosmic-btn disabled:opacity-60"
                >
                  {claiming ? "Revealing..." : "Reveal both answers"}
                </button>
              ) : (
                <div className="space-y-3">
                  {hasBothAnswers ? (
                    <>
                      <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                        <div className="text-xs font-extrabold tracking-wide text-white/65 uppercase mb-2">
                          {inviterDisplay}'s Answer
                        </div>
                        <div className="text-white">
                          {initiatorAnswer}
                        </div>
                      </div>
                      <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                        <div className="text-xs font-extrabold tracking-wide text-white/65 uppercase mb-2">
                          Your Answer
                        </div>
                        <div className="text-white">
                          {inviteeAnswer}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                      <div className="text-sm font-extrabold text-white">
                        Reveal is ready — pulling answers…
                      </div>
                      <div className="mt-1 text-sm ul-cosmic-muted">
                        Tap refresh if they don't appear in a second.
                      </div>
                      <button
                        onClick={fetchInvite}
                        className="mt-3 w-full ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110 py-2.5"
                      >
                        Refresh reveal
                      </button>
                    </div>
                  )}
                  <button
                    onClick={continueToMatch}
                    disabled={continuing}
                    className="w-full ul-cosmic-btn disabled:opacity-60"
                  >
                    {continuing ? "Continuing..." : "Continue"}
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
        </>
        )}

          </div>
        </div>
      </div>
    </div>
  );
}
